import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import EmploymentFilter, { type Employment } from '../components/EmploymentFilter';
import type { ExportJobRequest, ExportJob } from '../types/attendance';
import Pagination from '../components/Pagination';

interface UserPermissions {
  canExportAttendance: boolean;
  attendanceViewScope: 'self' | 'department' | 'company' | 'custom';
  viewableUserIds?: number[];
}

interface ViewableUser {
  id: number;
  name: string;
  username: string;
  employee_number: string;
  email?: string;
  department_name?: string;
  position_name?: string;
  /** 退職日時。null は在籍中。退職者も**出力の対象に含める** */
  retired_at?: string | null;
}

interface Department {
  id: number;
  name: string;
}

const AttendanceExportPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  /**
   * 対象者を選べない簡易モード。解約・トライアル切れで `/api/users` が 403 になったとき。
   * このときは全従業員・指定期間で1本だけ出す（サーバーが `'all'` を解決する）。
   */
  const [exportOnlyMode, setExportOnlyMode] = useState(false);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [viewableUsers, setViewableUsers] = useState<ViewableUser[]>([]);
  const [employment, setEmployment] = useState<Employment>('active');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [dateRangeType, setDateRangeType] = useState<'month' | 'custom'>('month');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format, setFormat] = useState<'csv' | 'pdf'>('csv');
  const [outputType, setOutputType] = useState<'merged' | 'separated'>('merged');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);
  const [userListPage, setUserListPage] = useState(1);
  const usersPerPage = 20;

  // 在職状態の絞り込みを変えたら読み直す。
  // 選択済みの対象者は**そのまま残す** — 在職者を選んでから退職者に切り替えて
  // 追加する、という使い方（退職者を含めた期間の一括出力）を潰さないため。
  useEffect(() => {
    loadInitialData();
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [employment]);

  const loadInitialData = async () => {
    try {
      // 権限情報取得
      const permsResponse = await api.get<UserPermissions>('/auth/permissions');
      setPermissions(permsResponse.data);

      // エクスポート権限がない場合はダッシュボードにリダイレクト
      if (!permsResponse.data.canExportAttendance) {
        navigate('/dashboard');
        return;
      }

      // 閲覧可能ユーザー一覧・部署一覧を取得する。
      //
      // ⚠️ **解約後はこの2つが 403 になる**（`/api/users` と `/api/departments` は
      // requireActiveSubscription の内側）。一方でエクスポート自体は
      // requireExportAccess で通るため、ここで落ちると
      // 「規約で約束した解約後30日のダウンロード」が画面から実行できなくなる。
      //
      // そこで 403 のときは**対象者を選ばない簡易モード**へ落とす。
      // サーバー側は `userIds: 'all'` を受け取り、閲覧可能な範囲を自分で解決する。
      try {
        // **在職者だけに固定しない**。記録を残していても出力できなければ
        // 論理削除にした意味が消える（退職者の勤怠は保存義務の対象で、
        // 必要になるのはむしろ退職の後）。既定は在職者、切り替えで退職者も出す。
        const usersResponse = await api.get('/users', { params: { employment } });
        setViewableUsers(usersResponse.data.users || []);

        const deptResponse = await api.get('/departments');
        setDepartments(deptResponse.data.departments || []);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setExportOnlyMode(true);
          setViewableUsers([]);
          setDepartments([]);
        } else {
          throw err;
        }
      }

      // デフォルトの日付範囲設定（今月）
      const now = new Date();
      const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      setSelectedMonth(yearMonth);
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      // タイムゾーンの影響を受けないように、年月日を直接フォーマット
      const formatLocalDate = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      setDateFrom(formatLocalDate(firstDay));
      setDateTo(formatLocalDate(lastDay));

      // アクティブなジョブを確認
      checkActiveJob();
    } catch (error) {
      console.error('初期データ取得エラー:', error);
      setMessage('データの取得に失敗しました');
    }
  };

  const checkActiveJob = async () => {
    try {
      const response = await api.get<{ job: ExportJob | null }>('/attendance/export/jobs/active');
      if (response.data.job) {
        setExportJob(response.data.job);

        // pending または processing の場合はポーリング開始
        if (response.data.job.status === 'pending' || response.data.job.status === 'processing') {
          setMessage('エクスポート処理を実行中です...');
          if (!pollingIntervalRef.current) {
            const interval = window.setInterval(() => {
              checkJobStatus();
            }, 3000);
            pollingIntervalRef.current = interval;
          }
        } else if (response.data.job.status === 'completed') {
          setMessage('エクスポートが完了しました。ダウンロードボタンをクリックしてください。');
        } else if (response.data.job.status === 'failed') {
          setMessage(`エクスポートに失敗しました: ${response.data.job.errorMessage || '不明なエラー'}`);
        }
      }
    } catch (error) {
      console.error('アクティブジョブ確認エラー:', error);
    }
  };

  // 部署でフィルタリング
  const selectedDepartmentName = departments.find(d => d.id === selectedDepartmentId)?.name;
  const filteredUsers = selectedDepartmentId
    ? viewableUsers.filter(u => u.department_name === selectedDepartmentName)
    : viewableUsers;

  // ページネーション
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (userListPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // 月選択が変更されたときに日付範囲を更新
  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    if (month) {
      const [year, monthNum] = month.split('-').map(Number);
      const firstDay = new Date(year, monthNum - 1, 1);
      const lastDay = new Date(year, monthNum, 0);

      // タイムゾーンの影響を受けないように、年月日を直接フォーマット
      const formatLocalDate = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      setDateFrom(formatLocalDate(firstDay));
      setDateTo(formatLocalDate(lastDay));
    }
  };

  const handleUserToggle = (userId: number) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const validateDates = (): boolean => {
    if (!dateFrom || !dateTo) {
      setMessage('日付範囲を選択してください');
      return false;
    }

    const from = new Date(dateFrom);
    const to = new Date(dateTo);

    if (from > to) {
      setMessage('開始日は終了日より前に設定してください');
      return false;
    }

    // 1年以内かチェック
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    if (from < oneYearAgo) {
      setMessage('日付範囲は1年以内で指定してください');
      return false;
    }

    const daysDiff = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      setMessage('日付範囲は最大365日までです');
      return false;
    }

    return true;
  };

  /**
   * エクスポートを実行できない状態か。
   *
   * 対象者の選択チェックは **簡易モードでは効かせない**。
   * 簡易モード（解約・トライアル切れで `/api/users` が 403）では
   * 対象者一覧を読み込まないので selectedUserIds は常に空になり、
   * 無条件に見るとボタンが永久に押せなくなる。
   * 送信側（handleExport）は既に簡易モードを考慮して `userIds: 'all'` を送っており、
   * ここだけが取り残されていた。判定を揃える。
   */
  const exportDisabled =
    isLoading ||
    (!exportOnlyMode && selectedUserIds.size === 0) ||
    !dateFrom ||
    !dateTo ||
    exportJob?.status === 'processing';

  const handleExport = async () => {
    // 簡易モード（解約後）は対象者を選べないので、選択チェックを飛ばす
    if (!exportOnlyMode && selectedUserIds.size === 0) {
      setMessage('出力対象のユーザーを選択してください');
      return;
    }

    if (!validateDates()) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const requestData = {
        format,
        outputType,
        startDate: dateFrom,
        endDate: dateTo,
        // 簡易モードでは 'all' を送り、閲覧可能な範囲をサーバー側で解決させる
        userIds: exportOnlyMode ? 'all' : Array.from(selectedUserIds)
      };

      const response = await api.post<{ job: ExportJob }>('/attendance/export/jobs', requestData);
      setExportJob(response.data.job);
      setMessage('エクスポート処理を開始しました。完了までお待ちください...');

      // ポーリング開始
      if (!pollingIntervalRef.current) {
        const interval = window.setInterval(() => {
          checkJobStatus();
        }, 3000);
        pollingIntervalRef.current = interval;
      }

    } catch (error: any) {
      setMessage(error.response?.data?.error || 'エクスポート処理の開始に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const checkJobStatus = async () => {
    try {
      const response = await api.get<{ job: ExportJob | null }>('/attendance/export/jobs/active');

      if (response.data.job) {
        setExportJob(response.data.job);

        if (response.data.job.status === 'completed') {
          setMessage('エクスポートが完了しました。ダウンロードボタンをクリックしてください。');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } else if (response.data.job.status === 'failed') {
          setMessage(`エクスポートに失敗しました: ${response.data.job.errorMessage || '不明なエラー'}`);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }
    } catch (error) {
      console.error('ジョブステータス確認エラー:', error);
    }
  };

  const handleCancel = async () => {
    const jobId = (exportJob as any)?.job_id || exportJob?.jobId;
    if (!jobId) return;

    try {
      await api.delete(`/attendance/export/jobs/${jobId}`);
      setMessage('エクスポートをキャンセルしました');
      setExportJob(null);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    } catch (error: any) {
      setMessage(error.response?.data?.error || 'キャンセルに失敗しました');
    }
  };

  const handleDownload = async () => {
    console.log('handleDownload called', exportJob);
    const jobId = (exportJob as any)?.job_id || exportJob?.jobId;
    if (!jobId) {
      console.log('No jobId found');
      return;
    }

    try {
      // 認証ヘッダー付きでファイルをダウンロード
      const response = await api.get(`/attendance/export/jobs/${jobId}/download`, {
        responseType: 'blob'
      });

      // Blobからダウンロードリンクを作成
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // ファイル名を取得（Content-Dispositionヘッダーから、または拡張子から推測）
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'attendance_export';

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      } else {
        // ファイル名が取得できない場合、フォーマットから拡張子を推測
        fileName = `attendance_export.${exportJob?.format || 'csv'}`;
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      // クリーンアップ
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setMessage(error.response?.data?.error || 'ダウンロードに失敗しました');
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>勤怠データエクスポート</h1>
        {/* 戻り先は簡易モードで変える。
          * 制限付きログイン中は PrivateRoute が /dashboard を
          * /subscription へ差し戻すため、「ダッシュボードに戻る」と書くと
          * 表示と実際の遷移先が食い違う。行き先の名前をそのまま出す。
          */}
        <button
          onClick={() => navigate(exportOnlyMode ? '/subscription' : '/dashboard')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {exportOnlyMode ? 'サブスクリプション画面に戻る' : 'ダッシュボードに戻る'}
        </button>
      </div>

      {/* エクスポート設定 */}
      <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '25px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>出力設定</h2>

        {/* 日付範囲 */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>出力期間</label>

          {/* 期間指定方法選択 */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="month"
                checked={dateRangeType === 'month'}
                onChange={(e) => setDateRangeType(e.target.value as 'month')}
                style={{ marginRight: '8px' }}
              />
              <span>月指定</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="custom"
                checked={dateRangeType === 'custom'}
                onChange={(e) => setDateRangeType(e.target.value as 'custom')}
                style={{ marginRight: '8px' }}
              />
              <span>期間指定</span>
            </label>
          </div>

          {/* 月選択 */}
          {dateRangeType === 'month' && (
            <div>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '200px'
                }}
              />
              <p style={{ marginTop: '8px', color: '#6c757d', fontSize: '13px' }}>
                選択した月の勤怠データを出力します
              </p>
            </div>
          )}

          {/* 期間指定 */}
          {dateRangeType === 'custom' && (
            <div>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <span>〜</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <p style={{ marginTop: '8px', color: '#6c757d', fontSize: '13px' }}>
                ※ 最大1年間まで選択可能です
              </p>
            </div>
          )}
        </div>

        {/* フォーマット選択 */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>出力形式</label>
          <div style={{ display: 'flex', gap: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="csv"
                checked={format === 'csv'}
                onChange={(e) => setFormat(e.target.value as 'csv')}
                style={{ marginRight: '8px' }}
              />
              <span>CSV</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="pdf"
                checked={format === 'pdf'}
                onChange={(e) => setFormat(e.target.value as 'pdf')}
                style={{ marginRight: '8px' }}
              />
              <span>PDF</span>
            </label>
          </div>
        </div>

        {/* 出力方法選択 */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>出力方法</label>
          <div style={{ display: 'flex', gap: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="merged"
                checked={outputType === 'merged'}
                onChange={(e) => setOutputType(e.target.value as 'merged')}
                style={{ marginRight: '8px' }}
              />
              <span>1つのファイルにまとめる</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="separated"
                checked={outputType === 'separated'}
                onChange={(e) => setOutputType(e.target.value as 'separated')}
                style={{ marginRight: '8px' }}
              />
              <span>従業員ごとに分割</span>
            </label>
          </div>
          {outputType === 'separated' && (
            <p style={{ marginTop: '8px', color: '#6c757d', fontSize: '13px' }}>
              ※ 複数ファイルはZIP形式で圧縮されます
            </p>
          )}
        </div>
      </div>

      {/* 対象ユーザー選択
        *
        * 解約後（簡易モード）は `/api/users` が 403 で一覧を作れないため、
        * 選択UIの代わりに「全従業員が対象になる」ことを明示する。
        * 規約で約束した解約後30日のダウンロードを、画面から実行できる状態に保つため。
        */}
      {exportOnlyMode ? (
        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '25px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>出力対象</h2>
          <p style={{ fontSize: '14px', color: '#6c757d', margin: 0, lineHeight: 1.8 }}>
            ご契約が終了しているため、従業員を個別に選択する画面はご利用いただけません。
            <br />
            <strong>在籍していた全従業員</strong>が出力対象になります。期間は上で指定してください。
          </p>
        </div>
      ) : (
      <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '25px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>出力対象の従業員選択</h2>

        {/* 部署フィルター */}
        {departments.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>部署で絞り込み</label>
            <select
              value={selectedDepartmentId || ''}
              onChange={(e) => {
                setSelectedDepartmentId(e.target.value ? parseInt(e.target.value) : null);
                setUserListPage(1); // 部署変更時はページをリセット
              }}
              style={{
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '200px'
              }}
            >
              <option value="">すべての部署</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* 在職状態の絞り込み */}
        <div style={{ marginBottom: '12px' }}>
          <EmploymentFilter
            id="export-employment"
            value={employment}
            onChange={setEmployment}
            count={viewableUsers.length}
          />
        </div>

        {/* 全選択ボタン */}
        <div style={{ marginBottom: '15px' }}>
          <button
            onClick={handleSelectAll}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {selectedUserIds.size === filteredUsers.length ? '全選択解除' : '全選択'}
          </button>
          <span style={{ marginLeft: '15px', color: '#6c757d' }}>
            {selectedUserIds.size} 人選択中
          </span>
        </div>

        {/* ユーザーリスト */}
        <div style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '10px'
        }}>
          {filteredUsers.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6c757d', padding: '20px' }}>
              閲覧可能なユーザーがいません
            </p>
          ) : (
            paginatedUsers.map(user => (
              <label
                key={user.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  backgroundColor: selectedUserIds.has(user.id) ? '#f0f8ff' : 'transparent'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedUserIds.has(user.id)}
                  onChange={() => handleUserToggle(user.id)}
                  style={{ marginRight: '12px', width: '18px', height: '18px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {user.name}
                    {user.retired_at && (
                      <span style={{ marginLeft: '8px', padding: '1px 6px', borderRadius: '4px', background: '#e9ecef', color: '#6c757d', fontSize: '11px', fontWeight: 'normal' }}>
                        退職
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6c757d' }}>
                    <span style={{ marginRight: '15px' }}>社員番号: {user.employee_number}</span>
                    {user.department_name && <span style={{ marginRight: '15px' }}>部署: {user.department_name}</span>}
                    {user.position_name && <span>役職: {user.position_name}</span>}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        {/* ページネーション */}
        {filteredUsers.length > usersPerPage && (
          <div style={{ marginTop: '20px' }}>
            <Pagination
              currentPage={userListPage}
              totalPages={totalPages}
              onPageChange={setUserListPage}
              itemsPerPage={usersPerPage}
              totalItems={filteredUsers.length}
            />
          </div>
        )}
      </div>
      )}

      {/* エクスポートボタン */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '25px' }}>
        <button
          onClick={handleExport}
          disabled={exportDisabled}
          style={{
            padding: '15px 40px',
            backgroundColor: exportDisabled ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: exportDisabled ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {isLoading || exportJob?.status === 'processing' ? '処理中...' : 'エクスポート実行'}
        </button>
      </div>

      {/* メッセージ表示 */}
      {message && (
        <div style={{
          padding: '15px',
          marginBottom: '20px',
          backgroundColor: message.includes('失敗') || message.includes('エラー') ? '#f8d7da' : '#d4edda',
          color: message.includes('失敗') || message.includes('エラー') ? '#721c24' : '#155724',
          borderRadius: '4px',
          border: `1px solid ${message.includes('失敗') || message.includes('エラー') ? '#f5c6cb' : '#c3e6cb'}`
        }}>
          {message}
        </div>
      )}

      {/* ジョブステータス表示 */}
      {exportJob && (
        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '15px' }}>エクスポート状況</h2>
          <div style={{ marginBottom: '15px' }}>
            <strong>ステータス: </strong>
            <span style={{
              padding: '4px 12px',
              borderRadius: '4px',
              backgroundColor:
                exportJob.status === 'completed' ? '#d4edda' :
                exportJob.status === 'failed' ? '#f8d7da' :
                exportJob.status === 'processing' ? '#fff3cd' : '#e7e7e7',
              color:
                exportJob.status === 'completed' ? '#155724' :
                exportJob.status === 'failed' ? '#721c24' :
                exportJob.status === 'processing' ? '#856404' : '#383d41'
            }}>
              {exportJob.status === 'pending' && '待機中'}
              {exportJob.status === 'processing' && '処理中'}
              {exportJob.status === 'completed' && '完了'}
              {exportJob.status === 'failed' && '失敗'}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            {exportJob.status === 'completed' && (
              <button
                onClick={handleDownload}
                style={{
                  padding: '12px 30px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 'bold'
                }}
              >
                ダウンロード
              </button>
            )}
            {(exportJob.status === 'pending' || exportJob.status === 'processing') && (
              <button
                onClick={handleCancel}
                style={{
                  padding: '12px 30px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 'bold'
                }}
              >
                キャンセル
              </button>
            )}
          </div>

          {/* ダウンロード可能期間表示（完了時のみ） */}
          {exportJob.status === 'completed' && ((exportJob as any).expires_at || exportJob.expiresAt) && (
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#333' }}>
              ダウンロード可能期間: {new Date((exportJob as any).expires_at || exportJob.expiresAt).toLocaleString('ja-JP', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })} まで
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceExportPage;
